import { useState, useEffect, useMemo } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  AUDIENCE,
  audienceHomePath,
  clearAudience,
  getAudience,
  setAudience,
} from "./audience";
import LoginModal from "./LoginModal";
import RegisterModal from "./RegisterModal";
import { MarketingAuthUiProvider } from "./MarketingAuthUi";
import "../style/scss/marketing.scss";

export default function MarketingShell({ children, updateLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const audience = getAudience();
  const isDecision = location.pathname === "/";
  const isLoginPath =
    location.pathname === "/login" || location.pathname === "/logout";
  const isRegisterPath = location.pathname === "/register";
  const [loginOpen, setLoginOpen] = useState(isLoginPath);
  const [registerOpen, setRegisterOpen] = useState(isRegisterPath);

  useEffect(() => {
    if (isLoginPath) {
      setLoginOpen(true);
      setRegisterOpen(false);
    }
    if (isRegisterPath) {
      setRegisterOpen(true);
      setLoginOpen(false);
    }
  }, [isLoginPath, isRegisterPath]);

  function switchAudience(next) {
    setAudience(next);
    navigate(audienceHomePath(next));
  }

  function onSwitchClick() {
    clearAudience();
    navigate("/");
  }

  function openLogin() {
    setRegisterOpen(false);
    setLoginOpen(true);
  }

  function openRegister() {
    setLoginOpen(false);
    setRegisterOpen(true);
  }

  function closeAuth() {
    setLoginOpen(false);
    setRegisterOpen(false);
    if (isLoginPath || isRegisterPath) {
      navigate("/", { replace: true });
    }
  }

  const authUi = useMemo(
    () => ({
      openLogin,
      openRegister,
      closeAuth,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable open/close for page CTAs
    [isLoginPath, isRegisterPath]
  );

  return (
    <MarketingAuthUiProvider value={authUi}>
      <div className="mkt-shell">
        <header className="mkt-header">
          <Link to="/" className="mkt-header__logo">
            CareerMaster
          </Link>
          <nav className="mkt-header__nav" aria-label="Marketing">
            {audience && (
              <>
                <NavLink to={audienceHomePath(audience)}>Overview</NavLink>
                <NavLink to="/how-it-works">How it works</NavLink>
              </>
            )}
            <NavLink to="/about">About</NavLink>
            <NavLink to="/faq">FAQ</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </nav>
          <div className="mkt-header__actions">
            {audience && (
              <button
                type="button"
                className="mkt-switch"
                onClick={onSwitchClick}
              >
                Switch audience
              </button>
            )}
            {audience === AUDIENCE.SEEKER && (
              <button
                type="button"
                className="mkt-switch mkt-switch--quiet"
                onClick={() => switchAudience(AUDIENCE.EMPLOYER)}
              >
                I&apos;m an employer
              </button>
            )}
            {audience === AUDIENCE.EMPLOYER && (
              <button
                type="button"
                className="mkt-switch mkt-switch--quiet"
                onClick={() => switchAudience(AUDIENCE.SEEKER)}
              >
                I&apos;m a job seeker
              </button>
            )}
            <button
              type="button"
              className={`mkt-header__login${isDecision ? " mkt-header__login--emphasis" : ""}`}
              onClick={openLogin}
            >
              Log in
            </button>
            {!isDecision && (
              <button
                type="button"
                className="mkt-btn mkt-btn--small mkt-btn--primary"
                onClick={openRegister}
              >
                Register
              </button>
            )}
          </div>
        </header>
        <main className="mkt-main">{children}</main>
        <footer className="mkt-footer">
          <Link to="/about">About</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/sms">SMS</Link>
          <Link to="/sms-opt-in">SMS opt-in</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <span className="mkt-footer__copy">© CareerMaster</span>
        </footer>
        {loginOpen && (
          <LoginModal
            updateLogin={updateLogin}
            onClose={closeAuth}
            onSwitchToRegister={openRegister}
          />
        )}
        {registerOpen && (
          <RegisterModal
            onClose={closeAuth}
            onSwitchToLogin={openLogin}
          />
        )}
      </div>
    </MarketingAuthUiProvider>
  );
}
